import { useMemo } from "react";
import { useAppSelector } from "../store/hooks";
import { isAdminApiEnabled } from "../config/adminApiFeature";
import { normalizeActivityList } from "../utils/dashboardActivity";
import { ADMIN_TASK_STATUS_OPTIONS } from "../screens/Admin/adminStatusUi";

function emptyStatusCounts() {
  return Object.fromEntries(
    ADMIN_TASK_STATUS_OPTIONS.map((o) => [o.value, 0])
  );
}

function baseVm(overrides = {}) {
  const z = emptyStatusCounts();
  return {
    source: "api",
    apiDisabled: false,
    loading: false,
    failed: false,
    unread: 0,
    unreadRequests: 0,
    unreadContacts: 0,
    requests: [],
    contacts: [],
    requestsCount: 0,
    contactsCount: 0,
    repliesTotal: 0,
    reqStatus: z,
    conStatus: z,
    newsletterYes: 0,
    aiStats: { none: 0, pending: 0, ok: 0, error: 0 },
    aiAttempts: 0,
    nReq: 0,
    aiSuccessRate: 0,
    aiFailRate: 0,
    emailAnalysis: {
      totalInboundReplies: 0,
      completed: 0,
      pending: 0,
      failed: 0,
      unreadWithClientReply: 0,
    },
    activity: [],
    ...overrides,
  };
}

/**
 * Dashboard metrics from Redux only (`GET /admin/dashboard/overview`).
 * Does not read or write localStorage. When the admin API flag is off, returns `apiDisabled: true`.
 */
export function useAdminOverviewData() {
  const apiOn = isAdminApiEnabled();
  const dashboard = useAppSelector((s) => s.dashboard);

  return useMemo(() => {
    if (!apiOn) {
      return baseVm({
        source: "disabled",
        apiDisabled: true,
      });
    }

    const { status, data: d } = dashboard;
    const loading = status === "loading" || status === "idle";

    if (status === "failed") {
      return baseVm({ failed: true, loading: false });
    }

    if (loading || !d) {
      return baseVm({ loading: true, failed: false });
    }

    const unread = d.unread?.total ?? 0;
    const unreadRequests = d.unread?.requests ?? 0;
    const unreadContacts = d.unread?.contacts ?? 0;
    const requestsCount = d.counts?.requests ?? 0;
    const contactsCount = d.counts?.contacts ?? 0;
    const repliesTotal = d.counts?.replies ?? 0;
    const reqStatus = {
      ...emptyStatusCounts(),
      ...(d.requestsByStatus || {}),
    };
    const conStatus = {
      ...emptyStatusCounts(),
      ...(d.contactsByStatus || {}),
    };
    const newsletterYes = d.counts?.newsletterOptIn ?? 0;
    const aiStats = {
      none: d.ai?.none ?? 0,
      pending: d.ai?.pending ?? 0,
      ok: d.ai?.ok ?? 0,
      error: d.ai?.error ?? 0,
    };
    const aiAttempts = d.ai?.attempts ?? d.ai?.attemptsTotal ?? 0;
    const nReq = requestsCount;
    const aiSuccessRate = nReq ? Math.round((aiStats.ok / nReq) * 100) : 0;
    const aiFailRate = nReq ? Math.round((aiStats.error / nReq) * 100) : 0;
    const activity = normalizeActivityList(d.activity);
    const emailAnalysis = {
      totalInboundReplies: d.emailAnalysis?.totalInboundReplies ?? d.emailAnalysis?.total ?? 0,
      completed: d.emailAnalysis?.completed ?? 0,
      pending: d.emailAnalysis?.pending ?? 0,
      failed: d.emailAnalysis?.failed ?? 0,
      unreadWithClientReply:
        d.emailAnalysis?.unreadWithClientReply ?? d.emailAnalysis?.unread ?? 0,
    };

    return {
      source: "api",
      apiDisabled: false,
      loading: false,
      failed: false,
      unread,
      unreadRequests,
      unreadContacts,
      requests: [],
      contacts: [],
      requestsCount,
      contactsCount,
      repliesTotal,
      reqStatus,
      conStatus,
      newsletterYes,
      aiStats,
      aiAttempts,
      nReq,
      aiSuccessRate,
      aiFailRate,
      emailAnalysis,
      activity,
    };
  }, [apiOn, dashboard]);
}
